import { useReactive } from 'ahooks'

import type { $Enums } from '@prisma/client'
import type {
  TriggerPurchaseSchemaInput,
  TriggerSignUpSchemaInput,
} from '@loc/server/interface/track/trackEventParams.common'

import { ProductsSubAppName } from '@/contexts/SiteSpecificContext/types'
import { useAnalyticsData } from '@/hooks/analyticsData'

export interface ISignUpData {
  onSignUpSuccess?: () => void
  onSignInSuccess?: () => void
  googleCallbackUrl?: string
  readOnlyEmail?: string
  defaultShowSignUp?: boolean
  triggerLocation?: TriggerSignUpSchemaInput['location']
  triggerType?: TriggerSignUpSchemaInput['type']
}
//
export type TUpgradeModalAction =
  | 'upgrade-by-usage-runout' // 非 unlimited用户 credit用完提示升级
  | 'upgrade-by-contact-unlimited-overage' // unlimited用户 credit使用超过一定值，提示联系我们
  | 'upgrade-by-reach-max-concurrent' // 非 unlimited 用户，并发超过限制，提示升级
  | 'upgrade-by-unlimited-reach-max-concurrent' // unlimited 用户，并发超过限制，仅显示提示文案

export function usePrivateAppUIModal() {
  /**
   * @description: 额度不够，提示升级，或联系我们
   */
  const upgradeModal = useReactive({
    open: false,
    data: {
      appName: ProductsSubAppName.App1,
      action: 'upgrade-by-usage-runout',
    } as {
      appName?: $Enums.SubLogAppName
      action: TUpgradeModalAction
    },
    show(data?: {
      appName: $Enums.SubLogAppName
      action: TUpgradeModalAction
    }) {
      if (data) {
        upgradeModal.data = Object.assign({}, upgradeModal.data, data)
      }
      upgradeModal.open = true
    },
    close() {
      upgradeModal.open = false
      upgradeModal.data = {
        appName: ProductsSubAppName.App1,
        action: 'upgrade-by-usage-runout',
      }
    },
  })

  const { onChangeUserIsSignUp, trackCustomEvent } = useAnalyticsData()

  /**
   * @description: 注册/登录弹窗
   */
  const signUp = useReactive({
    // open: true,
    open: false,
    data: {
      defaultShowSignUp: true,
    } as ISignUpData,

    show(data: ISignUpData) {
      signUp.open = true
      signUp.data = data
      if (data.triggerLocation && data.triggerType) {
        trackCustomEvent({
          name: 'trigger_sign_up',
          location: data.triggerLocation,
          type: data.triggerType,
        })
      }
    },
    close() {
      signUp.open = false
      signUp.data = {
        defaultShowSignUp: true,
      } as ISignUpData
    },
  })

  /**
   * @description:  套餐升级弹窗
   */
  const upgradePlan = useReactive({
    open: false,
    data: {
      appName: ProductsSubAppName.App1,
      reason: '',
      location: '' as TriggerPurchaseSchemaInput['location'],
      method:
        'click_Subscription_button' as TriggerPurchaseSchemaInput['method'],
      type: '' as string,
    } as {
      appName?: $Enums.SubLogAppName
      reason?: string
      location?: TriggerPurchaseSchemaInput['location']
      method?: TriggerPurchaseSchemaInput['method']
      type?: string
    },
    show(data?: {
      appName?: $Enums.SubLogAppName
      reason?: string
      location?: TriggerPurchaseSchemaInput['location']
      method?: TriggerPurchaseSchemaInput['method']
      type?: string
    }) {
      upgradePlan.data = {
        appName: ProductsSubAppName.App1,
        reason: '',
        location: '',
        method: 'click_Subscription_button',
        type: '',
        ...data,
      }
      trackCustomEvent({
        name: 'trigger_purchase',
        location: upgradePlan.data.location || '',
        method: upgradePlan.data.method || 'click_Subscription_button',
        type: upgradePlan.data.type || '',
      })
      upgradePlan.open = true
    },
    close() {
      upgradePlan.open = false
      upgradePlan.data = {
        appName: ProductsSubAppName.App1,
        reason: '',
        location: '',
        method: 'click_Subscription_button',
        type: '',
      }
    },
  })

  const profileModal = useReactive({
    // open: true,
    open: false,
    data: {} as {
      title?: React.ReactNode
      desc?: React.ReactNode
      onClose?: () => void
    },
    show(data?: {
      title?: React.ReactNode
      desc?: React.ReactNode
      onClose?: () => void
    }) {
      if (data) {
        profileModal.data = data
      }
      profileModal.open = true
    },
    close() {
      onChangeUserIsSignUp()
      profileModal.data.onClose?.()
      profileModal.open = false
      profileModal.data = {}
    },
  })

  const paymentConfirm = useReactive({
    open: false,
    data: {
      onConfirm: async () => ({ ok: true }),
    } as {
      onConfirm: () => Promise<{ ok: boolean }>
    },

    show(data: { onConfirm: () => Promise<{ ok: boolean }> }) {
      paymentConfirm.open = true
      paymentConfirm.data = data
    },
    close() {
      paymentConfirm.open = false
      paymentConfirm.data = {
        onConfirm: async () => ({ ok: true }),
      } as {
        onConfirm: () => Promise<{ ok: boolean }>
      }
    },
  })

  const contactUsModal = useReactive({
    // open: true,
    open: false,
    data: {} as {
      action?: 'upgrade' | 'cancel'
      title?: React.ReactNode
      desc?: React.ReactNode
      onClose?: () => void
    },
    show(data?: {
      action?: 'upgrade' | 'cancel'
      title?: React.ReactNode
      desc?: React.ReactNode
      onClose?: () => void
    }) {
      if (data) {
        contactUsModal.data = data
      }
      contactUsModal.open = true
    },
    close() {
      contactUsModal.open = false
      contactUsModal.data = {}
    },
  })

  return {
    upgradeModal,
    upgradePlan,
    signUp,
    profileModal,
    paymentConfirm,
    contactUsModal,
  }
}
